import {
    ChangeDetectionStrategy,
    Component,
    ComponentRef,
    EventEmitter,
    HostListener,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {NgForm} from '@angular/forms';
import {RestService} from '../services/rest.service';
import {UIBlockerService} from '../services/uiblocker.service';
import {DataService} from '../services/data.service';
import {finalize} from 'rxjs/operators';
import {isNullOrUndefined} from '../shared/is-null-or-undefined';
import {DialogGeneralComponent} from '../dialog-general/dialog-general.component';
import {environment} from '../../environments/environment';
import {TranslationService} from '../services/translation.service';
import {ApiResponse, Car, Driver} from '../models/api.models';
import {DialogText} from '../models/ui.models';
import {PermissionService} from '../services/permission.service';
import {AdminPermissions} from '../models/admin-permissions';
import {DialogService} from '../services/dialog.service';

@Component({
    selector: 'app-dialog-car',
    templateUrl: './dialog-car.component.html',
    styleUrls: ['./dialog-car.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DialogCarComponent implements OnInit {
    readonly isNullOrUndefined = isNullOrUndefined;

    @ViewChild('dialogsContainer2', {read: ViewContainerRef, static: true}) dialogsContainer2;
    dialogRef: ComponentRef<DialogGeneralComponent>;

    @Input() headerText: string;
    @Input() carItem: Car | Pick<Car, 'CarIdentifier'>;
    @Input() isExternal = false;

    @Output() onDialogConfirm: EventEmitter<void> = new EventEmitter<void>();
    @Output() onDialogCancel: EventEmitter<void> = new EventEmitter<void>();
    @Output() onCarUpdated: EventEmitter<void> = new EventEmitter<void>();

    changeSuspendReason: string;
    carDetails?: Car;
    sessionId?: string;
    canUnpairDevice = false;
    canUpdateCar = false;
    canSuspendCar = false;
    canUnsuspendCar = false;
    canSendCarMessage = false;
    shouldUnpairBeVisible = true;
    baseUrlForImages: string = environment.driverResourcesUrl;
    carIdentifier = '';
    showReason = false;
    suspendReason: string;
    suspensionHours: number;
    showPriceInput = false;
    priceInput: number;
    packageType: string;
    notificationMessage = '';
    notificationType = 'info';
    pushTitle = '';
    pushMessage = '';
    isSendingPush = false;
    pushSuccessMessage = '';
    pushErrorMessage = '';
    private notificationTimer?: ReturnType<typeof setTimeout>;

    constructor(private activatedRoute: ActivatedRoute,
                private restService: RestService,
                private uiBlockerService: UIBlockerService,
                private dataService: DataService,
                private router: Router,
                private translationService: TranslationService,
                private permissionService: PermissionService,
                private dialogService: DialogService) {
    }

    ngOnInit() {
        this.sessionId = this.dataService.getSessionIdentifier();

        this.checkPermissions();
        this.makeCarDetailsRequest();

        this.dataService.dataChangedSession.subscribe(() => {

            this.sessionId = this.dataService.getSessionIdentifier();

            this.checkPermissions();
            this.makeCarDetailsRequest();
        });
    }

    @HostListener('document:keydown.escape', ['$event'])
    onEscapeKey(event: KeyboardEvent) {
        if (event.defaultPrevented || this.hasActiveGeneralDialog() || this.dialogService.isOpen) {
            return;
        }

        event.preventDefault();
        this.onCloseClick();
    }

    checkPermissions() {
        this.canUnpairDevice = this.permissionService.has(AdminPermissions.UnpairCarDevice);
        this.canUpdateCar = this.permissionService.has(AdminPermissions.UpdateCar);
        this.canSuspendCar = this.permissionService.has(AdminPermissions.SuspendCar);
        this.canUnsuspendCar = this.permissionService.has(AdminPermissions.UnsuspendCar);
        this.canSendCarMessage = this.permissionService.has(AdminPermissions.SendCarMessage);
    }

    makeCarDetailsRequest() {
        if (!isNullOrUndefined(this.sessionId)) {
            this.getCarDetails();
        }
    }

    makeUnpairDeviceRequest() {

        if (this.canUnpairDevice) {
            this.unpairDevice();
        }
    }

    showUnpairDeviceDialog() {

        const dialogTexts: DialogText = {
            HeaderText: 'Unpair Device Confirmation',
            BodyText: 'Are you sure you want to unpair this device?',
            ConfirmButtonText: 'Yes',
            CancelButtonText: 'No'
        };

        this.dialogsContainer2.clear();
        this.dialogRef = this.dialogsContainer2.createComponent(DialogGeneralComponent);

        this.dialogRef.instance.headerText = dialogTexts.HeaderText;
        this.dialogRef.instance.bodyText = dialogTexts.BodyText;
        this.dialogRef.instance.confirmButtonText = dialogTexts.ConfirmButtonText;
        this.dialogRef.instance.cancelButtonText = dialogTexts.CancelButtonText;

        this.dialogRef.instance.onDialogConfirm.subscribe(() => {

            this.onUnpairConfirm();
            this.dialogRef.destroy();
        });
        this.dialogRef.instance.onDialogCancel.subscribe(() => {

            this.onUnpairCancel();
            this.dialogRef.destroy();
        });
    }

    testNewBtn() {
        if (!this.canUpdateCar) return;

        const dialogTexts: DialogText = {
            HeaderText: 'Drink Safe Confirmation',
            BodyText: 'Are you sure you want to enable drink safe?',
            ConfirmButtonText: 'Yes',
            CancelButtonText: 'No'
        };

        this.dialogsContainer2.clear();
        this.dialogRef = this.dialogsContainer2.createComponent(DialogGeneralComponent);

        this.dialogRef.instance.headerText = dialogTexts.HeaderText;
        this.dialogRef.instance.bodyText = dialogTexts.BodyText;
        this.dialogRef.instance.confirmButtonText = dialogTexts.ConfirmButtonText;
        this.dialogRef.instance.cancelButtonText = dialogTexts.CancelButtonText;

        this.dialogRef.instance.onDialogConfirm.subscribe(() => {

            this.updateDrinkSafe(true);
            this.dialogRef.destroy();
        });
        this.dialogRef.instance.onDialogCancel.subscribe(() => {

            this.updateDrinkSafe(false);
            this.dialogRef.destroy();
        });
    }

    selectPackage() {
        if (!this.canUpdateCar) return;

        const dialogTexts: DialogText = {
            HeaderText: 'Drink Safe Confirmation',
            BodyText: 'Alege pachet',
            ConfirmButtonText: 'Submit',
            CancelButtonText: 'Cancel',
            showPriceInput: true
        };

        this.dialogsContainer2.clear();
        this.dialogRef = this.dialogsContainer2.createComponent(DialogGeneralComponent);

        this.dialogRef.instance.headerText = dialogTexts.HeaderText;
        this.dialogRef.instance.bodyText = dialogTexts.BodyText;
        this.dialogRef.instance.confirmButtonText = dialogTexts.ConfirmButtonText;
        this.dialogRef.instance.cancelButtonText = dialogTexts.CancelButtonText;
        this.dialogRef.instance.showPriceInput = true;

        this.dialogRef.instance.onDialogConfirm.subscribe(() => {

            this.updatePackage();
            this.dialogRef.destroy();
        });
        this.dialogRef.instance.onDialogCancel.subscribe(() => {

            this.dialogRef.destroy();
        });
    }

    suspendCar() {
        if (!this.canSuspendCar) return;

        const dialogTexts: DialogText = {
            HeaderText: 'Suspend Confirmation',
            BodyText: 'Are you sure you want to suspend this driver?',
            ConfirmButtonText: 'Yes',
            CancelButtonText: 'No',
            showReason: true
        };

        this.dialogsContainer2.clear();
        this.dialogRef = this.dialogsContainer2.createComponent(DialogGeneralComponent);

        this.dialogRef.instance.headerText = dialogTexts.HeaderText;
        this.dialogRef.instance.bodyText = dialogTexts.BodyText;
        this.dialogRef.instance.confirmButtonText = dialogTexts.ConfirmButtonText;
        this.dialogRef.instance.cancelButtonText = dialogTexts.CancelButtonText;
        this.dialogRef.instance.showReason = true;

        this.dialogRef.instance.onDialogConfirm.subscribe(() => {

            this.suspendDriver();
            this.dialogRef.destroy();
        });
        this.dialogRef.instance.onDialogCancel.subscribe(() => {

            this.dialogRef.destroy();
        });
    }

    unsuspendCar() {
        if (!this.canUnsuspendCar) return;

        const dialogTexts: DialogText = {
            HeaderText: 'Unsuspend Confirmation',
            BodyText: 'Are you sure you want to unsuspend this driver?',
            ConfirmButtonText: 'Yes',
            CancelButtonText: 'No',

        };

        this.dialogsContainer2.clear();
        this.dialogRef = this.dialogsContainer2.createComponent(DialogGeneralComponent);

        this.dialogRef.instance.headerText = dialogTexts.HeaderText;
        this.dialogRef.instance.bodyText = dialogTexts.BodyText;
        this.dialogRef.instance.confirmButtonText = dialogTexts.ConfirmButtonText;
        this.dialogRef.instance.cancelButtonText = dialogTexts.CancelButtonText;

        this.dialogRef.instance.onDialogConfirm.subscribe(() => {

            this.unsuspendDriver();
            this.dialogRef.destroy();
        });
        this.dialogRef.instance.onDialogCancel.subscribe(() => {

            this.dialogRef.destroy();
        });
    }

    onUnpairConfirm() {
        this.makeUnpairDeviceRequest();
    }

    onUnpairCancel() {

        this.showNotification('Unpair was cancelled', 'info');
    }

    getCarDetails() {
        const request = this.isExternal
            ? this.restService.getExternalCarDetails(this.carItem.CarIdentifier)
            : this.restService.getCarDetails(this.carItem.CarIdentifier);
        request
            .pipe(
                finalize(() => {
                    this.uiBlockerService.unBlockUI();
                })
            ).subscribe({
            next: (res) => {
                if (this.dataService.isResponseSuccessful(res) && res.Car) {
                    this.carDetails = res.Car;
                    this.updateShouldUnpairBeVisible();
                }
            },
            error: () => undefined
        });
    }

    updateDrinkSafe(isDrinkSafe: boolean) {
        if (!this.canUpdateCar) return;

        const body = {
            IsDrinkSafe: isDrinkSafe,
            CarIdentifier: this.carDetails.CarIdentifier,
            CityId: this.carDetails.CityId,
            CompanyId: this.carDetails.CompanyIdentifierId,
        };
        const request = this.isExternal
            ? this.restService.updateExternalDrinkSafe(body)
            : this.restService.updateDrinkSafe(body);
        request.pipe(
            finalize(() => {
                this.uiBlockerService.unBlockUI();
            })
        ).subscribe({
            next: () => this.getCarDetails(),
            error: () => undefined
        });
    }

    updatePackage() {
        if (!this.canUpdateCar) return;

        const body = {
            Package: this.dialogRef.instance.packageType,
            CarIdentifier: this.carDetails.CarIdentifier,
            CityId: this.carDetails.CityId,
            CompanyId: this.carDetails.CompanyIdentifierId,
            Tariff: this.dialogRef.instance.priceInput,
        };

        const request = this.isExternal
            ? this.restService.updateExternalPackage(body)
            : this.restService.updatePackage(body);
        request.pipe(
            finalize(() => {
                this.uiBlockerService.unBlockUI();
            })
        ).subscribe({
            next: () => this.getCarDetails(),
            error: () => undefined
        });
    }

    suspendDriver() {
        if (!this.canSuspendCar) return;

        const body = {
            SuspensionType: 'complaint',
            CarId: this.carDetails.CarId,
            HoursCount: this.dialogRef.instance.suspensionHours,
            Reason: this.dialogRef.instance.suspendReason,
            CompanyIdentifier: this.carDetails.CompanyIdentifier,
            CityId: this.carDetails.CityId || 1,
            CompanyId: this.carDetails.CompanyIdentifierId || 4769,
        };

        this.restService.suspendDriver(body).pipe(
            finalize(() => {
                this.uiBlockerService.unBlockUI();
            })
        ).subscribe({
            next: () => {
                this.onCarUpdated.emit();
                this.getCarDetails();
            },
            error: () => undefined
        });
    }

    unsuspendDriver() {
        if (!this.canUnsuspendCar) return;

        const body = {
            SuspensionType: 'complaint',
            CarId: this.carDetails.CarId,
            CompanyIdentifier: this.carDetails.CompanyIdentifier,
            CityId: this.carDetails.CityId || 1,
            CompanyId: this.carDetails.CompanyIdentifierId || 4769
        };
        this.restService.unsuspendDriver(body).pipe(
            finalize(() => {
                this.uiBlockerService.unBlockUI();
            })
        ).subscribe({
            next: () => {
                this.onCarUpdated.emit();
                this.getCarDetails();
            },
            error: () => undefined
        });
    }

    updateShouldUnpairBeVisible() {
        if (this.carDetails.DeviceId === '' && this.carDetails.PairingIdentifier === '') {
            this.shouldUnpairBeVisible = false;
        }

    }

    getDriverPhone(driver: Driver | undefined): string {
        if (isNullOrUndefined(driver)) {
            return '';
        }

        return this.normalizePhoneNumber(driver.DriverPhone);
    }

    normalizePhoneNumber(phoneNumber: unknown): string {
        if (isNullOrUndefined(phoneNumber)) {
            return '';
        }

        const trimmedPhoneNumber = String(phoneNumber).trim();
        const prefix = trimmedPhoneNumber.charAt(0) === '+' ? '+' : '';
        const digits = trimmedPhoneNumber.replace(/\D/g, '');

        return digits === '' ? '' : prefix + digits;
    }

    unpairDevice() {
        if (!this.canUnpairDevice) return;

        const request = this.isExternal
            ? this.restService.unpairExternalDevice(this.carItem.CarIdentifier)
            : this.restService.unpairDevice(this.carItem.CarIdentifier);
        request
            .pipe(
                finalize(() => {
                    this.uiBlockerService.unBlockUI();
                })
            ).subscribe({
            next: (res: ApiResponse) => {
                if (this.dataService.isResponseSuccessful(res)) {
                    this.showNotification('Unpair was completed with success', 'success');
                } else {
                    this.showNotification('Failed to make the unpair', 'danger');
                }
            },
            error: () => {
                this.showNotification('Failed to make the unpair', 'danger');
            }
        });
    }

    async sendNotificationToCar(form: NgForm): Promise<void> {
        if (!this.canSendCarMessage) return;

        this.pushSuccessMessage = '';
        this.pushErrorMessage = '';

        const title = this.pushTitle.trim();
        const message = this.pushMessage.trim();
        const carIdentifier = this.carDetails?.CarIdentifier?.trim() || '';
        if (form.invalid || title === '' || message === '' || carIdentifier === '') {
            this.pushErrorMessage = 'Enter both a title and a message.';
            return;
        }

        const confirmed = await this.dialogService.confirm({
            headerText: 'Message This Car',
            bodyText: 'Send this push notification to this car?',
            confirmButtonText: 'Send to car',
            cancelButtonText: 'Cancel',
            danger: false
        });
        if (!confirmed) return;

        this.isSendingPush = true;
        this.restService.messageCar({
            CarIdentifier: carIdentifier,
            Title: title,
            Message: message
        }).pipe(
            finalize(() => {
                this.isSendingPush = false;
            })
        ).subscribe({
            next: response => {
                if (!this.dataService.isResponseSuccessful(response)) {
                    this.pushErrorMessage = response.StatusMessage || 'The message could not be sent.';
                    return;
                }

                form.resetForm({pushTitle: '', pushMessage: ''});
                this.pushSuccessMessage = response.StatusMessage || 'Message sent to car successfully.';
            },
            error: (error: HttpErrorResponse) => {
                const response = error.error as ApiResponse | undefined;
                this.pushErrorMessage = response && response.StatusMessage
                    ? response.StatusMessage
                    : 'The message could not be sent.';
            }
        });
    }

    onCancelClick() {
        this.onDialogCancel.emit();
    }

    onHide() {
        this.onDialogCancel.emit();
    }

    onCloseClick() {
        this.onDialogCancel.emit();
    }

    onDriverSelected(driverIdentifier: string) {

        if (!isNullOrUndefined(driverIdentifier)) {
            void this.router.navigate(['/central/driver/' + driverIdentifier], {
                queryParams: {
                    carIdentifier: this.carItem.CarIdentifier,
                    external: this.isExternal
                }
            });
        }
    }

    useFallbackDriverImage(event: Event) {
        const image = event.target as HTMLImageElement;
        image.onerror = null;
        image.src = 'assets/images/no_image.png';
    }

    showNotification(messageReceived: string, typeReceived: string) {
        clearTimeout(this.notificationTimer);
        this.notificationMessage = this.translationService.translate(messageReceived);
        this.notificationType = typeReceived;
        this.notificationTimer = setTimeout(() => {
            this.notificationMessage = '';
        }, 2000);
    }

    private hasActiveGeneralDialog() {
        return !isNullOrUndefined(this.dialogRef) && !this.dialogRef.hostView.destroyed;
    }

}
